const DIRECT_PROFILE_ROUTE_TYPES = {
  personal_profile_direct: "personal",
  business_profile_direct: "business",
};

export function get_id_from_profile_slug(slug) {
  return String(slug || "").match(/-(\d+)$/)?.[1] || "";
}

function first_present(values) {
  return values.find((value) => value !== undefined && value !== null && String(value).trim() !== "") || "";
}

function get_profile_type_from_path(path) {
  return String(path || "").match(/^\/profile\/([^/]+)/)?.[1] || "";
}

function get_profile_type_from_route(route) {
  return DIRECT_PROFILE_ROUTE_TYPES[route.name] || get_profile_type_from_path(route.path);
}

function get_route_user_id(route, profile_type, profile_slug) {
  const slug_id = get_id_from_profile_slug(profile_slug);
  return first_present([
    route.query.user_id,
    profile_type === "personal" ? slug_id : "",
  ]);
}

export function get_profile_route_context(route) {
  const profile_slug = first_present([route.params.profile_slug, route.query.profile]);
  const profile_type = first_present([get_profile_type_from_route(route), route.query.type]);

  return {
    profile_slug,
    profile_type,
    user_id: get_route_user_id(route, profile_type, profile_slug),
  };
}

function get_query_base_payload(route) {
  return route.query.base ? { base_profile_id: route.query.base } : null;
}

function get_business_slug_payload(profile_type, profile_slug) {
  return profile_type === "business" && profile_slug
    ? { base_profile_id: get_id_from_profile_slug(profile_slug) }
    : null;
}

function get_slug_payload(profile_type, profile_slug) {
  return profile_slug ? { profile_slug, profile_type: profile_type || "personal" } : null;
}

function get_user_payload(user_id) {
  return user_id ? { user_id } : {};
}

export function get_profile_request_payload({ route, profile_type, profile_slug, user_id }) {
  return ({
    ...get_query_base_payload(route),
    ...get_business_slug_payload(profile_type, profile_slug),
    ...get_slug_payload(profile_type, profile_slug),
    ...get_user_payload(user_id),
  });
}

function is_plain_object(value) {
  return Boolean(value) && value.constructor === Object;
}

function get_social_link_url(link = {}) {
  return String(link.url || "").trim();
}

function has_social_platform(link = {}) {
  return Boolean(link.platform);
}

function has_social_url(link = {}) {
  return get_social_link_url(link) !== "";
}

function has_social_link_value(link = {}) {
  return has_social_platform(link) || has_social_url(link);
}

function is_complete_social_link(link = {}) {
  return has_social_platform(link) && has_social_url(link);
}

function is_incomplete_social_link(link = {}) {
  return has_social_link_value(link) && !is_complete_social_link(link);
}

function get_saved_social_profiles(response = {}) {
  return Array.isArray(response.data) ? response.data : [response.data];
}

function social_profiles_match(profile_link = {}, saved_link = {}) {
  return profile_link.id === saved_link.id
    || profile_link.social_type?.id === saved_link.social_type?.id;
}

function get_existing_social_profile_index(profile_links = [], saved_link = {}) {
  return profile_links.findIndex((profile_link) => social_profiles_match(profile_link, saved_link));
}

export const customer_profile_social_methods = {
  has_pending_social_links() {
    return this.new_socials.some(has_social_link_value);
  },
  has_incomplete_social_links() {
    return this.new_socials.some(is_incomplete_social_link);
  },
  get_social_profile_save_settings(options = {}) {
    const settings = is_plain_object(options) ? options : {};

    return ({
      require_link: settings.require_link !== false,
      hide_modal: settings.hide_modal !== false,
      show_error: settings.show_error !== false,
    });
  },
  get_valid_social_links() {
    return this.new_socials.filter(is_complete_social_link);
  },
  reject_incomplete_social_links() {
    this.errors.social_link = "Please complete the platform and link for each social profile.";
    return false;
  },
  accept_social_links() {
    this.errors.social_link = "";
    return true;
  },
  resolve_empty_social_links(settings) {
    this.set_empty_social_link_error(settings);
    return !settings.require_link;
  },
  set_empty_social_link_error(settings) {
    if (!settings.require_link || !settings.show_error) return;
    this.errors.social_link = "Please enter at least one valid social link.";
  },
  validate_social_profile_links(valid_links, settings) {
    if (this.has_incomplete_social_links()) {
      return this.reject_incomplete_social_links();
    }

    if (valid_links.length > 0) {
      return this.accept_social_links();
    }

    return this.resolve_empty_social_links(settings);
  },
  build_social_profile_payload(valid_links) {
    return ({
      base_profile_id: this.profileData?.base_profile?.id,
      social_profiles: valid_links.map(link => ({
        social_type_id: link.platform,
        social_link: get_social_link_url(link)
      })),
    });
  },
  ensure_profile_social_links() {
    if (!this.profileData?.social_profiles) {
      this.profileData.social_profiles = [];
    }
  },
  upsert_profile_social_link(saved_link) {
    const existing_index = get_existing_social_profile_index(this.profileData.social_profiles, saved_link);

    if (existing_index === -1) {
      this.profileData.social_profiles.push(saved_link);
      return;
    }

    this.profileData.social_profiles.splice(existing_index, 1, saved_link);
  },
  upsert_profile_social_links(saved_profiles = []) {
    this.ensure_profile_social_links();
    saved_profiles.forEach((saved_link) => {
      this.upsert_profile_social_link(saved_link);
    });
  },
  close_social_modal_if_needed(hide_modal) {
    if (!hide_modal) return;
    $("#social_modal").modal("hide");
  },
  handle_social_profiles_save_success(response, hide_modal) {
    const saved_profiles = get_saved_social_profiles(response);
    this.upsert_profile_social_links(saved_profiles);
    this.new_socials = [{ platform: "", url: "" }];
    this.close_social_modal_if_needed(hide_modal);
  },
  handle_social_profiles_save_response(response, settings, resolve) {
    if (response.status !== "success") {
      resolve(false);
      return;
    }

    this.handle_social_profiles_save_success(response, settings.hide_modal);
    resolve(true);
  },
  handle_social_profiles_save_error(error, resolve) {
    console.error("Error saving social links:", error.responseJSON?.message || error);
    resolve(false);
  },
  save_social_profiles(options = {}) {
    const settings = this.get_social_profile_save_settings(options);
    const valid_links = this.get_valid_social_links();
    const is_valid = this.validate_social_profile_links(valid_links, settings);

    if (!is_valid || !valid_links.length) {
      return Promise.resolve(is_valid);
    }

    const payload = this.build_social_profile_payload(valid_links);

    return new Promise((resolve) => {
      $.ajax({
        type: "POST",
        url: `${process.env.VUE_APP_BACKEND_API_URL}/company_management/create_social_profile/`,
        headers: {
          Authorization: `Token ${this.getToken()}`,
        },
        data: JSON.stringify(payload),
        contentType: "application/json",
        success: (response) => this.handle_social_profiles_save_response(response, settings, resolve),
        error: (error) => this.handle_social_profiles_save_error(error, resolve)
      });
    });
  },
};
